import React, { useState, useEffect } from "react";
import { useAppStore } from "@/store/appContext";
import { ShopifyProduct } from "@/types";
import ProductCard from "./ProductCard";

const ProductGrid: React.FC = () => {
  const store = useAppStore();
  const {
    products,
    isLoading,
    localCart,
    updateLocalCartItem,
    clearProductDropdownsReset,
  } = store;
  const shouldResetDropdowns = store.resetProductDropdowns;

  // State to track selected variant and quantity for each product
  const [productStates, setProductStates] = useState<
    Record<
      string,
      {
        selectedVariantId: string;
        quantity: number;
      }
    >
  >({});

  // State to track user-selected selling plans for each variant
  const [userSelectedSellingPlans, setUserSelectedSellingPlans] = useState<
    Record<string, string | null>
  >({});

  // Reset product states when resetProductDropdowns is triggered
  useEffect(() => {
    if (shouldResetDropdowns) {
      setProductStates({});
      setUserSelectedSellingPlans({});
      clearProductDropdownsReset();
    }
  }, [shouldResetDropdowns, clearProductDropdownsReset]);

  // Helper function to get product state or initialize it
  const getProductState = (product: ShopifyProduct) => {
    if (!productStates[product.id]) {
      const initialState = {
        selectedVariantId: product.variants.edges[0].node.id,
        quantity: 0,
      };
      setProductStates((prev) => ({ ...prev, [product.id]: initialState }));
      return initialState;
    }
    return productStates[product.id];
  };

  // Helper function to get selling plan for a specific variant from cart or user selection
  // Defaults to "One-time" (undefined) if no selection has been made
  const getVariantSellingPlan = (
    product: ShopifyProduct,
    variantId: string
  ) => {
    const variantKey = `${product.id}-${variantId}`;

    // Check if user has explicitly selected a selling plan for this variant
    if (
      Object.prototype.hasOwnProperty.call(userSelectedSellingPlans, variantKey)
    ) {
      return userSelectedSellingPlans[variantKey] || undefined;
    }

    // Check cart for existing selling plan
    const cartItem = localCart.items.find(
      (item) => item.productId === product.id && item.variantId === variantId
    );

    if (cartItem?.sellingPlanId) {
      return cartItem.sellingPlanId;
    }

    // Default to "One-time" (undefined) - no auto-selection
    return undefined;
  };

  // Helper function to get current quantity from local cart for a specific variant
  const getCurrentQuantity = (productId: string, variantId: string) => {
    const cartItem = localCart.items.find(
      (item) => item.productId === productId && item.variantId === variantId
    );
    return cartItem ? cartItem.quantity : 0;
  };

  // Handle quantity change (now synchronous!)
  const handleQuantityChange = (product: ShopifyProduct, change: number) => {
    const productState = getProductState(product);
    const currentQuantity = getCurrentQuantity(
      product.id,
      productState.selectedVariantId
    );
    const newQuantity = Math.max(0, currentQuantity + change);

    // Get the selling plan for the current variant (defaults to "One-time" if not selected)
    const variantSellingPlan = getVariantSellingPlan(
      product,
      productState.selectedVariantId
    );

    // Update local cart immediately - no API calls!
    updateLocalCartItem(
      product.id,
      productState.selectedVariantId,
      newQuantity,
      variantSellingPlan
    );
  };

  // Handle variant selection
  const handleVariantChange = (product: ShopifyProduct, variantId: string) => {
    // Update local state
    setProductStates((prev) => ({
      ...prev,
      [product.id]: {
        ...prev[product.id],
        selectedVariantId: variantId,
      },
    }));

    // Note: We don't automatically transfer quantity between variants
    // Each variant maintains its own quantity in the cart
    // This allows users to have different quantities for different variants
  };

  // Handle selling plan selection
  const handleSellingPlanChange = (
    product: ShopifyProduct,
    variantId: string,
    sellingPlanId: string
  ) => {
    const variantKey = `${product.id}-${variantId}`;

    // Track user's explicit selection
    setUserSelectedSellingPlans((prev) => ({
      ...prev,
      [variantKey]: sellingPlanId || null,
    }));

    // Update cart item if it exists for this variant
    const currentQuantity = getCurrentQuantity(product.id, variantId);
    if (currentQuantity > 0) {
      updateLocalCartItem(
        product.id,
        variantId,
        currentQuantity,
        sellingPlanId || undefined
      );
    }
    // Note: If quantity is 0, we don't update the cart yet
    // The selling plan will be applied when the user adds items to cart
  };

  if (isLoading) {
    return (
      <div className="product-grid-container">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="product-grid-container">
      <div className="product-grid">
        {products.map((product: ShopifyProduct) => {
          const productState = getProductState(product);
          const currentQuantity = getCurrentQuantity(
            product.id,
            productState.selectedVariantId
          );
          const currentSellingPlan = getVariantSellingPlan(
            product,
            productState.selectedVariantId
          );

          return (
            <ProductCard
              key={product.id}
              product={product}
              selectedVariantId={productState.selectedVariantId}
              selectedSellingPlanId={currentSellingPlan}
              currentQuantity={currentQuantity}
              onVariantChange={(variantId) =>
                handleVariantChange(product, variantId)
              }
              onQuantityChange={(change) =>
                handleQuantityChange(product, change)
              }
              onSellingPlanChange={(sellingPlanId) =>
                handleSellingPlanChange(
                  product,
                  productState.selectedVariantId,
                  sellingPlanId
                )
              }
            />
          );
        })}
      </div>
    </div>
  );
};

export default ProductGrid;
